/**
 * Kanban Task Card Component
 *
 * Individual task card for the Kanban board.
 */

import { useMemo } from 'react';
import { Card, Space, Typography, Avatar, Tooltip } from 'antd';
import {
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { StyledTag } from '@components/common';
import type { ProcessInstance } from '@/types';

const { Text } = Typography;

interface KanbanCardProps {
  task: ProcessInstance;
  onClick: (task: ProcessInstance) => void;
  isDragging?: boolean;
}

export default function KanbanCard({ task, onClick, isDragging }: KanbanCardProps) {
  const isOverdue = useMemo(() => {
    return task.status !== 'completed' && new Date(task.dueDate) < new Date();
  }, [task.dueDate, task.status]);

  const daysUntilDue = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(task.dueDate);
    due.setHours(0, 0, 0, 0);
    const diff = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  }, [task.dueDate]);

  const dueDateDisplay = useMemo(() => {
    if (daysUntilDue === 0) return 'Today';
    if (daysUntilDue === 1) return 'Tomorrow';
    if (daysUntilDue === -1) return 'Yesterday';
    if (daysUntilDue < 0) return `${Math.abs(daysUntilDue)} days ago`;
    if (daysUntilDue <= 7) return `${daysUntilDue} days`;
    return new Date(task.dueDate).toLocaleDateString();
  }, [daysUntilDue, task.dueDate]);

  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getAvatarColor = (id: string) => {
    const colors = [
      '#f56a00',
      '#7265e6',
      '#ffbf00',
      '#00a2ae',
      '#1890ff',
      '#eb2f96',
      '#52c41a',
      '#722ed1',
    ];
    return colors[id.charCodeAt(0) % colors.length];
  };

  return (
    <Card
      size="small"
      onClick={() => onClick(task)}
      hoverable
      style={{
        marginBottom: 8,
        cursor: 'pointer',
        opacity: isDragging ? 0.5 : 1,
        borderLeft: isOverdue ? '3px solid var(--status-error)' : undefined,
      }}
      styles={{
        body: { padding: 12 },
      }}
    >
      {/* Title */}
      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        {task.template?.title || 'Untitled Task'}
      </Text>

      {/* Category and Tags */}
      <div style={{ marginBottom: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {task.template?.category && (
          <StyledTag
            name={task.template.category.name}
            color={task.template.category.color}
            style={{ margin: 0 }}
          />
        )}
        {task.template?.tags?.slice(0, 3).map((tag) => (
          <StyledTag
            key={tag.id}
            name={tag.name}
            color={tag.color}
            style={{ margin: 0 }}
          />
        ))}
        {task.template?.tags && task.template.tags.length > 3 && (
          <span style={{
            fontSize: 12,
            color: '#888',
            alignSelf: 'center',
            marginLeft: 4,
          }}>
            +{task.template.tags.length - 3}
          </span>
        )}
      </div>

      {/* Footer: Due date and assignee */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Space size={4}>
          {isOverdue ? (
            <ExclamationCircleOutlined style={{ color: 'var(--status-error)' }} />
          ) : (
            <ClockCircleOutlined style={{ color: daysUntilDue <= 2 ? 'var(--status-warning)' : 'var(--text-tertiary)' }} />
          )}
          <Text
            type={isOverdue ? 'danger' : daysUntilDue <= 2 ? 'warning' : 'secondary'}
            style={{ fontSize: 12 }}
          >
            {dueDateDisplay}
          </Text>
        </Space>

        {task.assignedUser ? (
          <Tooltip title={task.assignedUser.fullName}>
            <Avatar
              size={24}
              style={{
                backgroundColor: getAvatarColor(task.assignedUser.id),
                fontSize: 11,
              }}
            >
              {getInitials(task.assignedUser.fullName)}
            </Avatar>
          </Tooltip>
        ) : (
          <Tooltip title="Unassigned">
            <Avatar size={24} icon={<UserOutlined />} style={{ backgroundColor: 'var(--text-muted)' }} />
          </Tooltip>
        )}
      </div>
    </Card>
  );
}
